import { useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { API_FORGOT_PASSWORD_URL } from '../utils';
import { apiErrorMessage, isApiCode } from '../utils/apiErrors';
import TurnstileWidget, { turnstileEnabled } from '../components/common/TurnstileWidget';

const { Text } = Typography;

// The API answers 202 to a registered address, an unknown one, an inactive
// account, a repeat inside the cooldown and a dead mailer alike — anything
// else would turn the endpoint into an account-existence oracle. The UI has
// to keep that promise, so the copy below never varies with the outcome.
//
// The two things the API DOES answer differently are about the caller, not
// the address: the bot challenge (400 challenge_failed / 503
// challenge_unavailable) and the rate limit (429). Those are shown.
const NEUTRAL_CONFIRMATION = 'If an account exists for that address, a reset link is on its way.';

/**
 * Public "email me a reset link" form. Its one real job beyond the POST is
 * saying the same thing every time.
 */
export default function ForgotPassword() {
	const [status, setStatus] = useState('form');
	const [submitting, setSubmitting] = useState(false);
	const [errorText, setErrorText] = useState(null);
	const [turnstileToken, setTurnstileToken] = useState(null);
	const [turnstileReset, setTurnstileReset] = useState(0);

	const handleFinish = ({ email }) => {
		setSubmitting(true);
		setErrorText(null);

		api.post(API_FORGOT_PASSWORD_URL, { email, ...(turnstileToken ? { turnstileToken } : {}) })
			.then(() => setStatus('sent'))
			.catch(error => {
				const challenge = isApiCode(error, 'challenge_failed') || isApiCode(error, 'challenge_unavailable');
				const limited = error?.response?.status === 429;
				if (challenge || limited) {
					// About the caller, not the address — safe to show.
					setErrorText(apiErrorMessage(error));
					setTurnstileReset(key => key + 1);
				} else if (error?.response) {
					// If the server answered at all, its answer tells us nothing we
					// are allowed to show — same confirmation.
					setStatus('sent');
				} else {
					setStatus('unreachable');
				}
			})
			.finally(() => setSubmitting(false));
	};

	if (status === 'sent') {
		return (
			<>
				<h1>Forgot password</h1>
				<Alert
					type="success"
					showIcon
					message="Check your email"
					description={NEUTRAL_CONFIRMATION}
				/>
				<p style={{ marginTop: 16 }}>
					<Text type="secondary">
						The link is good for one hour. If it does not arrive, check your spam folder
						before trying again.
					</Text>
				</p>
				<p>
					<Link className="hyperlink" to="/login">
						Back to login
					</Link>
				</p>
			</>
		);
	}

	return (
		<>
			<h1>Forgot password</h1>

			<p>
				<Text type="secondary">
					Enter the email address on your account and we will send you a link to choose a
					new password.
				</Text>
			</p>

			<Form className="m-1" layout="vertical" onFinish={handleFinish}>
				<Form.Item
					name="email"
					label="Email"
					rules={[{ required: true, message: 'Enter your email address.' }]}
				>
					<Input autoCapitalize="none" autoComplete="username" type="email" />
				</Form.Item>

				{turnstileEnabled() ? (
					<TurnstileWidget action="forgot-password" onToken={setTurnstileToken} resetKey={turnstileReset} />
				) : null}

				{status === 'unreachable' ? (
					<Alert
						type="error"
						showIcon
						message="We couldn't reach the server. Check your connection and try again."
						style={{ marginBottom: 16 }}
					/>
				) : null}

				{errorText ? (
					<Alert type="error" showIcon message={errorText} style={{ marginBottom: 16 }} />
				) : null}

				<Button type="primary" htmlType="submit" loading={submitting}>
					Send reset link
				</Button>
			</Form>

			<p style={{ marginTop: 16 }}>
				<Link className="hyperlink" to="/login">
					Back to login
				</Link>
			</p>
		</>
	);
}
